
import React, { useState, useEffect } from "react";
import ItemSelectInput from "./ItemSelectInput.tsx";
import {
  SimpleForm,
  useDataProvider,
  useNotify,
} from "react-admin";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
const CalculateForm2 = () => {
  var currentValue: { id: any; name: string; raw: any } | null = null;
  const [productRows, setProductRows] = useState<
    { id: string; product: any | null; cans: string | number }[]
  >([{ id: "r0", product: null, cans: "" }]);
  const [rawMaterials, setRawMaterials] = useState<any[]>([]);
  const [finishedGoods, setFinishedGoods] = useState<any[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [result, setResult] = useState<Record<string, number | string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5180/api";
  // 当 productRows 变化时，根据选中的 productIds 去后端查询这些产品需要的物料


  useEffect(() => {
    setLoading(true);

    Promise.all([
      // 获取成品（FinishedGoods）
      dataProvider.getList("bom/getFinishedGoodsSelectInput", {
        pagination: { page: 1, perPage: 10000 },
        sort: { field: "productId", order: "ASC" },
        filter: { type: "FinishedGood" },
      }),
      // // 获取对应原料（RawMaterials）
      // dataProvider.getList("bom/getFinishedGoodsSelectInput", {
      //   pagination: { page: 1, perPage: 10000 },
      //   sort: { field: "productId", order: "ASC" },
      //   filter: { type: "RawMaterial", productIds },
      // }),
    ])
      .then(([finishedGoodsRes]) => {
        setFinishedGoods(finishedGoodsRes.data || []);
        // setRawMaterials(rawMaterialsRes.data || []);
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [dataProvider, API_URL]); // 注意这里：依赖 productRows

  useEffect(() => {
    // 取出当前已选择的 productIds
    const productIds = productRows
      .map((r) => (r.product ? (r.product.productId ?? r.product.id) : null))
      .filter((id): id is string | number => !!id);

    if (!productIds.length) {
      setRawMaterials([]);
      setLoading(false);
      return;
    }

    Promise.all([
      dataProvider.getList("bom/getFinishedGoodsSelectInput", {
        pagination: { page: 1, perPage: 10000 },
        sort: { field: "productId", order: "ASC" },
        filter: { type: "RawMaterial", productIds },
      }),
    ])
      .then(([rawMaterialsRes]) => {
        setRawMaterials(rawMaterialsRes.data || []);
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [dataProvider, productRows]); // 注意这里：依赖 productRows

  if (loading) return <span>Loading...</span>;
  if (error) return <span>Error loading options</span>;

  const addProductRow = () => {
    setProductRows((rows) => [
      ...rows,
      { id: `r${Date.now()}`, product: null, cans: 0 },
    ]);
  };

  const removeProductRow = (id: string) => {
    setProductRows((rows) => rows.filter((r) => r.id !== id));
  };

  const updateProductRow = (id: string, patch: Partial<{ product: any | null; cans: string | number }>) => {
    setProductRows((rows) =>
      rows.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );
    //update RawMaterials options when product changed
  };

  // 计算逻辑：
  // 假设每个 product 对象包含：
  // - blendSize (kg / blend)
  // - size (kg per can) 或 cansPerBlend 可推算为 Math.floor(blendSize/size)
  // - materials: [{ materialId, qtyPerBlend }] 表示每个 blend 需要多少 kg 的该物料
  const calculate = async () => {
    // 验证至少有一个 product 且 cans > 0
    if (!productRows.length || productRows.every((r) => !r.product || r.cans === "" || Number(r.cans) <= 0)) {
      notify("请至少添加一个产品并输入大于0的罐数", { type: "warning" });
      return;
    }
    if (!selectedMaterials.length) {
      notify("请至少选择一个物料", { type: "warning" });
      return;
    }

    // 构建要发送到后端的 payload，只包含需要的字段
    const productsPayload = productRows
      .map((r) => {
        if (!r.product) return null;
        const pid = r.product.productId ?? r.product.id;
        return {
          productId: pid,
          cans: Number(r.cans) || 0,
        };
      })
      .filter((p): p is { productId: string | number; cans: number } => p !== null && p.cans > 0);

    if (!productsPayload.length) {
      notify("Please provide valid products and quantities.", { type: "warning" });
      return;
    }

    // setLoading(true);
    try {
      const token =
        localStorage.getItem("token");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const resp = await fetch(`${API_URL}/bom/calculate-materials`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          products: productsPayload,
          materials: selectedMaterials, // 物料 id 列表
        }),
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `HTTP ${resp.status}`);
      }

      // 期望后端返回形如: { totals: { "<materialId>": number | string, ... } }
      const data = await resp.json();
      const totals = data.totals ?? data;

      // 将数字格式化为两位小数（保留 unknown 或字符串）
      Object.keys(totals).forEach((k) => {
        if (typeof totals[k] === "number") {
          totals[k] = Number((totals[k] as number).toFixed(2));
        }
      });

      setResult(totals);


    } catch (e: any) {
      notify(`计算失败: ${e?.message ?? e}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container " style={{ padding: 0, marginTop: '0!important' }}>
      <SimpleForm toolbar={false} sx={{
        "& .MuiCardContent-root:last-child": {
          paddingBottom: "0px !important",
        },
      }} className="calculate-page-container">
        <div className="" style={{ padding: 0, marginBottom: 0 }}>
          <h5>Products (add multiple)</h5>
          {productRows.map((row, idx) => (
            <div key={`productRow-${idx}`} className="d-flex align-items-center mb-2" style={{ gap: 12 }}>
              <div style={{ flex: 1, width: 300 }}>
                <ItemSelectInput
                  showSearch
                  source={`product-${row.id}`}
                  value={row.product}
                  choices={finishedGoods}
                  onChange={(value) => {
                    // console.info("Selected product:", value);
                    // setSelectedProduct(value);
                    updateProductRow(row.id, { product: value });
                  }}
                  sx={{
                    "& .MuiAutocomplete-inputRoot": {
                      minHeight: 50,
                      height: 50,
                      alignItems: "center",
                    },
                    "& .MuiInputBase-root": {
                      minHeight: 50,
                      height: 50,
                      alignItems: "center",
                    },
                    "& .MuiInputBase-input": {
                      height: 50,
                      paddingTop: 0,
                      paddingBottom: 0,
                      boxSizing: "border-box",
                    },
                  }}
                />
              </div>
              <div style={{ width: 160, position: "relative", top: -8 }}>
                <div className="input-group">
                  <input
                    type="number"
                    min={0}
                    className="form-control"
                    value={row.cans}
                    onChange={(e) => updateProductRow(row.id, { cans: e.target.value })} // 保存字符串，允许为空
                    placeholder="number of cans"
                    aria-label="cans"
                    style={{ height: 50, }}
                  />
                  <span className="input-group-text">can</span>
                </div>
              </div>
              <div>
                <button style={{ position: "relative", top: -8 }} type="button" className="btn btn-danger" onClick={() => removeProductRow(row.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}

          <button type="button" className="btn btn-primary mb-3" onClick={addProductRow}>
            Add Product
          </button>
        </div>

        <div className="mb-3">
          <h5>Select Materials</h5>

          {/* 可搜索多选 Autocomplete，按 productId - name 搜索 */}
          <Autocomplete
            multiple
            options={rawMaterials.map((m) => ({
              id: m.productId ?? m.id,
              name: `${m.productId ?? m.id} - ${m.name}`,
              raw: m,
            }))}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            value={rawMaterials
              .filter((m) => selectedMaterials.includes(String(m.productId ?? m.id)))
              .map((m) => ({ id: m.productId ?? m.id, name: `${m.productId ?? m.id} - ${m.name}`, raw: m }))}
            onChange={(_e, values) => {
              const ids = values.map((v: any) => String(v.id));
              setSelectedMaterials(ids);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="search by productId or name"
              />
            )}
            sx={{ minHeight: 80, minWidth: 468 }}
          />

          {/* <small className="form-text text-muted">可输入 productId 搜索，按住 Ctrl/Cmd 或 Shift 可多选。</small> */}
        </div>

        <div className="mb-3" style={{ marginBottom: 0 }}>
          <button type="button" className="btn btn-primary" onClick={calculate}>
            Calculate Raw Materials

          </button>
        </div>
      </SimpleForm>

      <div style={{ marginTop: 2, marginLeft: 15, position: "relative", top: -20 }}>
        <h5>Materials required (kg)</h5>
        {selectedMaterials.length === 0 ? (
          <p>No materials selected</p>
        ) : (
          <ul>
            {selectedMaterials.map((mid) => {
              const mat = rawMaterials.find((m) => String(m.productId ?? m.id) === String(mid));
              const name = mat ? `${mat.productId ?? mat.id} - ${mat.name}` : mid;
              const val = result[mid];
              return (
                <li key={mid}>
                  {name}:{" "}
                  <strong>
                    {val === undefined ? "0.00" : typeof val === "number" ? `${val.toFixed ? val.toFixed(2) : val}` : val}(kg)
                  </strong>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CalculateForm2;
